import _ from "lodash";
import cloneDeep from "lodash/cloneDeep";
import { call, put, SagaGenerator, select, take, takeEvery } from "typed-redux-saga";
import { Action } from "typescript-fsa";

import { ActivityActions, ActivityMap, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Model, ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { StoreSagas } from "@com.mgmtp.a12.client/client-core/lib/core/store";
import { FormActivity } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { DocumentModel } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { CHANGELOG_ACTIVITY } from "../../modules/changelog/constants";
import { dataModels as fristenDataModels } from "../../modules/fristen/moduleResources";
import { dataModels as wiedervorlagenDataModels } from "../../modules/wiedervorlagen/moduleResources";
import {
	ANTRAG_DETAIL_ACTIVITY_ID,
	TASK_DETAIL_ACTIVITY_ID,
	taskDocumentLoaded
} from "../../modules/processOverview/store/actions";
import { isDocumentWithIdAndProcessMetadata } from "../../modules/workflows/data/processMetadata";
import { assertNonNullable, assertTrue } from "../../utilities/assert";
import { selectDocument } from "../../utilities/storeHelpers";

import { updateIntegratedDocument, UpdateIntegratedDocumentPayload } from "./actions";
import {
	EXTRACT_SUBDOCUMENT_GROUP,
	EXTRACT_SUBDOCUMENT_ID,
	EXTRACT_SUBDOCUMENT_SUBGROUP,
	EXTRACT_SUBDOCUMENT_TRIGGER
} from "./annotations";

/**
 * Lädt integrierte Dokumente ("Subdokumente") in das Antrags/Prüfungs/...-Formular.
 * Hierfür werden im Dokumentenmodell mit "extractSubdocument..." annotierte Gruppen gesucht und geprüft, ob diese im Dokument
 * bereits mit einem existierenden Dokument verknüpft sind. Ist das der Fall, wird das Dokument vom Server geladen und die
 * enthaltenen Daten in das übergeordenete Dokument (z.B. Prozess_WF-Dokument) "verpflanzt"
 */
export function* initialiseIntegratedDocumentsInTaskDocumentsSaga(): SagaGenerator<void> {
	yield* takeEvery(taskDocumentLoaded, function* () {
		yield* call(initialiseIntegratedDocumentsSaga, TASK_DETAIL_ACTIVITY_ID);
	});
}
export function* initialiseLoadIntegratedDocumentsInDocumentsSaga(): SagaGenerator<void> {
	while (true) {
		const {
			payload: { activityId }
		} = yield* take(ActivityActions.loadData);
		const activity = yield* select(ActivitySelectors.activityById(activityId));
		assertNonNullable(activity);

		if (
			!activity.descriptor["instance"] ||
			!("model" in activity.descriptor) ||
			activity.id === CHANGELOG_ACTIVITY
		) {
			continue;
		}

		const document = yield* call(() => StoreSagas.waitForStateChange(selectDocument(activityId)));
		assertNonNullable(document);

		yield* call(initialiseIntegratedDocumentsSaga, activity.id);
	}
}

function* initialiseIntegratedDocumentsSaga(activityId: string): SagaGenerator<void> {
	const activity = yield* select(ActivitySelectors.activityById(activityId));

	// Wir können an dieser Stelle davon ausgehen, dass die activity da ist.
	// Wenn nicht, dann muss ein Fehler vorliegen.
	assertNonNullable(activity);

	if (activity.descriptor.instance) {
		const model = yield* select(
			ModelSelectors.modelByName(activity.descriptor.instance.split("/")[0], Model.isDocumentModel)
		);
		if (
			!model ||
			!([fristenDataModels.FRIST_D].includes(model.header.id) || model.header.id.endsWith("_Prozess_WF"))
		) {
			return;
		}

		// Suche Gruppen im Modell, die in ein eigenes Dokument ("Subdocument") extrahiert werden sollen.
		const subdocumentGroupInfos = findSubdocumentGroups(model.content.modelRoot);
		if (subdocumentGroupInfos.length === 0) {
			return;
		}

		// Wir müssen das Dokument klonen, um unerwartete Mutationen des Originaldokuments zu verhindern.
		const data = cloneDeep(yield* select(ActivitySelectors.data(activity.id)));

		// An dieser Stelle können die Daten nicht null sein
		assertNonNullable(data);
		assertTrue(FormActivity.Data.SingleDocumentData.isInstance(data));

		for (const subdocumentGroupInfo of subdocumentGroupInfos) {
			const subdocumentId: string = _.get(data.document, subdocumentGroupInfo.subdocumentIdPath);
			if (!subdocumentId && isDocumentWithIdAndProcessMetadata(data.document)) {
				// Noch kein extrahiertes Dokument -- Anlagedatum und Prozess-ID zur Vorbereitung initialisieren
				const fachverfahrenId = activity.descriptor.fachverfahren;
				assertNonNullable(fachverfahrenId);

				const subdocument = {
					ProcessId: data.document.root.processMetadata.processId,
					correlatedFachverfahrenId: fachverfahrenId,
					correlatedFachverfahrenKey: data.document.root.processMetadata.fachverfahren.key,
					correlatedFachverfahrenClient: data.document.root.processMetadata.fachverfahren.client
				};

				_.set(
					data.document,
					subdocumentGroupInfo.subdocumentInnerGroupPath,
					_.merge({}, _.get(data.document, subdocumentGroupInfo.subdocumentInnerGroupPath), subdocument)
				);
			}
		}

		yield* call(setDocument, activity.id, data, data.document);

		if (activityId === TASK_DETAIL_ACTIVITY_ID) {
			const antragActivity = yield* select(ActivitySelectors.activityById(ANTRAG_DETAIL_ACTIVITY_ID));
			if (antragActivity) {
				yield* call(setDocument, ANTRAG_DETAIL_ACTIVITY_ID, data, data.document);
			}
		}
	}
}

function* setDocument(activityid: string, data: object, document: object) {
	yield* put(
		ActivityActions.setData({
			activityId: activityid,
			data: {
				...data,
				document
			}
		})
	);
}

/**
 * Aktualisiert integrierte Dokumente in Formularen (Antrag, Prüfung oder WVL in Frist etc.), wenn die Dokumente
 * andernorts (z.B. in einem Modal oder einer anderen Ansicht) bearbeitet wurden
 */
export function* updateRelatedActivitiesOnSavingIntegratedDocumentSaga(): SagaGenerator<void> {
	yield* takeEvery(
		updateIntegratedDocument,
		function* ({
			payload: { subdocument, subdocumentId, processId, mode }
		}: Action<UpdateIntegratedDocumentPayload>) {
			const subdocumentModelId = subdocument.modelId;
			const activityMap: ActivityMap = yield* select(ActivitySelectors.activities());
			const relatedActivities = ActivityMap.toList(activityMap).filter(
				it => it.descriptor.model !== subdocumentModelId && it.descriptor.instance
			);
			for (const relatedActivity of relatedActivities) {
				const relatedData: object | undefined = yield* select(ActivitySelectors.data(relatedActivity.id));
				if (
					!FormActivity.Data.SingleDocumentData.isInstance(relatedData) ||
					!isDocumentWithIdAndProcessMetadata(relatedData.document) ||
					processId !== relatedData.document.root.processMetadata.processId
				) {
					continue;
				}

				if (!relatedActivity.descriptor.instance) {
					continue;
				}

				const model = yield* select(
					ModelSelectors.modelByName(relatedActivity.descriptor.instance.split("/")[0], Model.isDocumentModel)
				);
				if (!model) {
					continue;
				} // Das Modell muss bereits geladen sein, da das Formular bereits sichtbar ist.

				const subdocumentGroupInfos = findSubdocumentGroups(model.content.modelRoot);

				const documentClone = _.cloneDeep(relatedData.document);
				for (const subdocumentGroupInfo of subdocumentGroupInfos) {
					if (
						_.get(relatedData.document, subdocumentGroupInfo.subdocumentIdPath) === subdocumentId &&
						subdocument.modelId === subdocumentGroupInfo.subdocumentModelName
					) {
						_.set(documentClone, `${subdocumentGroupInfo.subdocumentGroupPath}.AlreadyExists`, true);
						_.set(documentClone, subdocumentGroupInfo.subdocumentTriggerPath, true);
						if (mode === "merge") {
							const existingData = _.get(documentClone, subdocumentGroupInfo.subdocumentInnerGroupPath);
							_.set(
								documentClone,
								subdocumentGroupInfo.subdocumentInnerGroupPath,
								_.merge({}, existingData, _.cloneDeep(subdocument.root))
							);
						} else {
							// Override
							_.set(
								documentClone,
								subdocumentGroupInfo.subdocumentInnerGroupPath,
								_.cloneDeep(subdocument.root)
							);
						}
						if (subdocumentModelId === wiedervorlagenDataModels.WIEDERVORLAGE_D) {
							_.unset(
								documentClone,
								`${subdocumentGroupInfo.subdocumentInnerGroupPath}.correlatedFachverfahrenShortName`
							);
						}
					}
				}
				yield* put(
					ActivityActions.setData({
						activityId: relatedActivity.id,
						data: { ...relatedData, document: documentClone }
					})
				);
			}
		}
	);
}

/** Findet die mit "extractSubdocument..." annotierten Gruppen in einem Dokumentenmodell.
 * Diese Gruppen sollen beim Speicher serverseitig in eigene Dokumente ("Subdocuments") extrahiert werden.
 * Rekursiv.
 * @param group Die aktuelle Gruppe, bei Erstaufruf die Wurzelgruppe des Modells.
 * @param currentPath Der Pfad zur aktuellen Gruppe.
 */
export function findSubdocumentGroups(
	group: DocumentModel.Group,
	currentPath = ""
): {
	subdocumentIdPath: string;
	subdocumentInnerGroupPath: string;
	subdocumentTriggerPath: string;
	subdocumentModelName: string;
	subdocumentGroupPath: string;
}[] {
	if (group.annotations?.some(e => e.name === EXTRACT_SUBDOCUMENT_GROUP)) {
		const pathPrefix = currentPath.substring(1); // remove initial "."

		const subdocumentModelName = group.annotations?.find(e => e.name === EXTRACT_SUBDOCUMENT_GROUP)?.value;
		if (!subdocumentModelName) {
			return [];
		}

		const subdocumentIdField = group.elements.find(
			it => it.type === "Field" && it.annotations?.some(e => e.name === EXTRACT_SUBDOCUMENT_ID)
		);
		if (!subdocumentIdField) {
			return [];
		}

		const subdocumentTriggerField = group.elements.find(
			it => it.type === "Field" && it.annotations?.some(e => e.name === EXTRACT_SUBDOCUMENT_TRIGGER)
		);
		if (!subdocumentTriggerField) {
			return [];
		}

		const subdocumentInnerGroup = group.elements.find(
			it => it.type === "Group" && it.annotations?.some(e => e.name === EXTRACT_SUBDOCUMENT_SUBGROUP)
		);
		if (!subdocumentInnerGroup) {
			return [];
		}

		return [
			{
				subdocumentIdPath: `${pathPrefix}.${subdocumentIdField.name}`,
				subdocumentInnerGroupPath: `${pathPrefix}.${subdocumentInnerGroup.name}`,
				subdocumentModelName,
				subdocumentTriggerPath: `${pathPrefix}.${subdocumentTriggerField.name}`,
				subdocumentGroupPath: pathPrefix
			},
			...group.elements
				.filter(it => it.type === "Group" && it.repeatability === 1)
				.flatMap(it => findSubdocumentGroups(it as DocumentModel.Group, `${currentPath}.${it.name}`))
		];
	} else {
		return group.elements
			.filter(it => it.type === "Group" && it.repeatability === 1)
			.flatMap(it => findSubdocumentGroups(it as DocumentModel.Group, `${currentPath}.${it.name}`));
	}
}

export const integratedDocumentSagas = [
	initialiseIntegratedDocumentsInTaskDocumentsSaga,
	initialiseLoadIntegratedDocumentsInDocumentsSaga,
	updateRelatedActivitiesOnSavingIntegratedDocumentSaga
];
